import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { adminAPI } from '@/lib/adminApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  ArrowLeft, Loader2, AlertCircle, Download, FileImage, 
  File, User, Clock, Package, Send, PlayCircle, CheckCircle, XCircle, Save, AlertTriangle,
  Upload, X, FileArchive, Video, RefreshCw, Eye, MessageSquare, Settings, Info, ClipboardList,
  CreditCard, FileText
} from 'lucide-react';
import { KeyValueGrid, KeyValueItem } from '@/components/admin/KeyValueGrid';
import { formatServiceDetails, formatCurrency, formatPaymentState, SERVICE_TYPE_LABELS as SERVICE_LABELS } from '@/lib/formatServiceDetails';
import { toast } from 'sonner';

// Service type labels
const SERVICE_TYPE_LABELS = {
  photo_restoration: 'Photo Restoration',
  lora_training: 'LoRA Training',
  photo_scanning: 'Photo Scanning',
  digital_persona: 'Digital Persona',
  general_upload: 'General Upload',
};

// Status config - matches backend OrderStatus enum
const STATUS_CONFIG = {
  draft: { label: 'Draft', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: Clock },
  received: { label: 'Received', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Send },
  in_progress: { label: 'In Progress', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: PlayCircle },
  delivered: { label: 'Delivered', className: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  canceled: { label: 'Canceled', className: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
  // Legacy statuses
  pending: { label: 'Pending', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  paid: { label: 'Paid', className: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  completed: { label: 'Completed', className: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
};

// Valid admin-selectable statuses (excludes draft)
const ADMIN_STATUSES = [
  { value: 'received', label: 'Received' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'canceled', label: 'Canceled' },
];

// Max file size for admin uploads (300MB)
const MAX_ADMIN_FILE_SIZE = 300 * 1024 * 1024;

// Revision kind options
const REVISION_KINDS = [
  { value: 'preview', label: 'Preview' },
  { value: 'final', label: 'Final' },
  { value: 'reference_sheet', label: 'Reference Sheet' },
  { value: 'output', label: 'Output' },
];

// Revision status config
const REVISION_STATUS_CONFIG = {
  pending: { label: 'Pending Review', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  approved: { label: 'Approved', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  changes_requested: { label: 'Changes Requested', className: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
};

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Get file icon based on extension
function getFileIcon(filename) {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (['safetensors', 'ckpt', 'pt', 'pth', 'bin'].includes(ext)) {
    return FileArchive; // Model files
  }
  if (['zip', 'tar', 'gz', 'tgz', 'rar'].includes(ext)) {
    return FileArchive;
  }
  if (['mp4', 'mov', 'avi', 'mkv'].includes(ext)) {
    return Video;
  }
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'tiff'].includes(ext)) {
    return FileImage;
  }
  return File;
}

export default function AdminOrderDetail() {
  const { orderId } = useParams();
  const { isAdmin } = useAuth();
  const fileInputRef = useRef(null);
  const revisionFileInputRef = useRef(null);
  
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Deliverable upload state
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  // Plan & Revision state
  const [planCode, setPlanCode] = useState('');
  const [maxRevisions, setMaxRevisions] = useState(0);
  const [savingPlan, setSavingPlan] = useState(false);
  
  // Revisions list
  const [revisions, setRevisions] = useState([]);
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  
  // Revision upload state
  const [revisionFile, setRevisionFile] = useState(null);
  const [revisionKind, setRevisionKind] = useState('preview');
  const [uploadingRevision, setUploadingRevision] = useState(false);
  const [revisionUploadProgress, setRevisionUploadProgress] = useState(0);

  // Confirmation dialog state
  const [showDeliverConfirm, setShowDeliverConfirm] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      loadOrderData();
    }
  }, [isAdmin, orderId]);

  // Sync selected status when order data loads
  useEffect(() => {
    if (orderData?.order?.status) {
      setSelectedStatus(orderData.order.status);
    }
    // Sync plan data
    if (orderData?.order) {
      setPlanCode(orderData.order.plan?.code || '');
      setMaxRevisions(orderData.order.plan?.max_revisions || 0);
    }
  }, [orderData]);

  // Load revisions when order loads
  useEffect(() => {
    if (orderData?.order?.id) {
      loadRevisions();
    }
  }, [orderData?.order?.id]);

  const loadOrderData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminAPI.getOrder(orderId);
      setOrderData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRevisions = async () => {
    try {
      setLoadingRevisions(true);
      const data = await adminAPI.listRevisions(orderId);
      setRevisions(data);
    } catch (err) {
      console.error('Failed to load revisions:', err);
    } finally {
      setLoadingRevisions(false);
    }
  };

  const handleStatusUpdate = async (skipConfirm = false) => {
    if (!selectedStatus || selectedStatus === orderData?.order?.status) return;
    
    // Show confirmation dialog for "delivered" status
    if (selectedStatus === 'delivered' && !skipConfirm) {
      setShowDeliverConfirm(true);
      return;
    }
    
    try {
      setUpdatingStatus(true);
      const updatedOrder = await adminAPI.updateOrderStatus(orderId, selectedStatus);
      
      // Update local state
      setOrderData(prev => ({
        ...prev,
        order: updatedOrder,
      }));
      
      toast.success('Status updated successfully', {
        description: `Order status changed to "${STATUS_CONFIG[selectedStatus]?.label || selectedStatus}"`,
      });
    } catch (err) {
      toast.error('Failed to update status', {
        description: err.message,
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleConfirmDeliver = async () => {
    setShowDeliverConfirm(false);
    await handleStatusUpdate(true);
  };

  const handleDownload = async (file) => {
    try {
      const { download_url } = await adminAPI.getFileDownloadUrl(file.id);
      window.open(download_url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    
    // Validate files
    const validFiles = [];
    for (const file of files) {
      if (file.size > MAX_ADMIN_FILE_SIZE) {
        toast.error(`File too large: ${file.name}`, {
          description: `Maximum size is ${formatFileSize(MAX_ADMIN_FILE_SIZE)}`,
        });
        continue;
      }
      validFiles.push(file);
    }
    
    if (validFiles.length > 0) {
      setUploadingFiles(prev => [...prev, ...validFiles]);
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeUploadingFile = (index) => {
    setUploadingFiles(prev => prev.filter((_, i) => i !== index));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[index];
      return newProgress;
    });
  };

  const handleUploadDeliverables = async () => {
    if (uploadingFiles.length === 0) return;
    
    const toastId = toast.loading(`Uploading ${uploadingFiles.length} file(s)...`);
    let successCount = 0;
    let errorCount = 0;
    
    for (let i = 0; i < uploadingFiles.length; i++) {
      const file = uploadingFiles[i];
      try {
        await adminAPI.uploadDeliverable(
          orderId,
          file,
          (percent) => {
            setUploadProgress(prev => ({ ...prev, [i]: percent }));
          }
        );
        successCount++;
      } catch (err) {
        console.error('Upload error:', err);
        errorCount++;
        toast.error(`Failed to upload ${file.name}`, {
          description: err.message,
        });
      }
    }
    
    // Clear upload state and reload
    setUploadingFiles([]);
    setUploadProgress({});
    
    if (successCount > 0) {
      toast.success(`Uploaded ${successCount} file(s) successfully`, { id: toastId });
      // Reload order data to show new files
      loadOrderData();
    } else {
      toast.error('All uploads failed', { id: toastId });
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // PLAN & REVISION HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════

  const handleSavePlan = async () => {
    try {
      setSavingPlan(true);
      const updatedOrder = await adminAPI.updateOrderPlan(orderId, planCode || null, maxRevisions);
      
      // Update local state
      setOrderData(prev => ({
        ...prev,
        order: updatedOrder,
      }));
      
      toast.success('Plan updated successfully', {
        description: `Max revisions set to ${maxRevisions}`,
      });
    } catch (err) {
      toast.error('Failed to update plan', {
        description: err.message,
      });
    } finally {
      setSavingPlan(false);
    }
  };

  const handleRevisionFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (file.size > MAX_ADMIN_FILE_SIZE) {
      toast.error('File too large', {
        description: `Maximum size is ${formatFileSize(MAX_ADMIN_FILE_SIZE)}`,
      });
      return;
    }
    
    setRevisionFile(file);
    
    // Reset input
    if (revisionFileInputRef.current) {
      revisionFileInputRef.current.value = '';
    }
  };

  const handleUploadRevision = async () => {
    if (!revisionFile) return;
    
    try {
      setUploadingRevision(true);
      setRevisionUploadProgress(0);
      
      await adminAPI.uploadRevision(
        orderId,
        revisionFile,
        revisionKind,
        (percent) => setRevisionUploadProgress(percent)
      );
      
      toast.success('Revision uploaded successfully', {
        description: `${REVISION_KINDS.find(k => k.value === revisionKind)?.label} revision created`,
      });
      
      // Clear and reload
      setRevisionFile(null);
      setRevisionUploadProgress(0);
      loadRevisions();
      loadOrderData(); // Refresh order to get updated revision_state
    } catch (err) {
      toast.error('Failed to upload revision', {
        description: err.message,
      });
    } finally {
      setUploadingRevision(false);
    }
  };

  const handleDownloadRevision = async (revision) => {
    try {
      const { download_url } = await adminAPI.getFileDownloadUrl(revision.file_id);
      window.open(download_url, '_blank');
    } catch (err) {
      toast.error('Download failed', {
        description: err.message,
      });
    }
  };

  // Redirect non-admins
  if (!isAdmin) {
    return <Navigate to="/orders" replace />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin" />
      </div>
    );
  }

  // Error state
  if (error || !orderData) {
    return (
      <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center text-center px-4">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <h3 className="text-lg text-white mb-2">Failed to load order</h3>
        <p className="text-[#E8E4DE]/60 mb-4">{error}</p>
        <Button asChild variant="outline" className="border-white/10 text-[#E8E4DE]">
          <Link to="/admin">Back to Dashboard</Link>
        </Button>
      </div>
    );
  }

  if (!orderData.order) {
    return <Navigate to={`/admin/real-estate-orders/${orderId}`} replace />;
  }

  const { order, files } = orderData;
  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.draft;
  const referenceFiles = files.filter(f => f.kind === 'reference');
  const outputFiles = files.filter(f => f.kind === 'output');

  return (
    <div className="min-h-screen bg-[#050816]">
      {/* Header */}
      <div className="bg-[#0f1724] border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <Button 
            asChild
            variant="ghost" 
            className="mb-4 text-[#E8E4DE]/60 hover:text-white hover:bg-white/5 -ml-2"
          >
            <Link to="/admin">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Link>
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-light text-white tracking-wide">
                  {order.title || SERVICE_TYPE_LABELS[order.service_type] || order.service_type}
                </h1>
                <Badge className={statusConfig.className}>
                  {statusConfig.label}
                </Badge>
              </div>
              <p className="text-[#E8E4DE]/60">
                {SERVICE_TYPE_LABELS[order.service_type]} • Order ID: {order.id.substring(0, 8)}...
              </p>
            </div>
            
            <Link 
              to={`/admin/clients/${order.user_id}`}
              className="text-[#C6A14A] hover:text-[#D4AF5A] text-sm flex items-center gap-1"
            >
              <User className="h-4 w-4" />
              View Customer
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Status Management Card */}
        <Card className="bg-[#0f1724] border-white/10" data-testid="status-management">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <PlayCircle className="h-5 w-5 text-[#C6A14A]" />
              Order Status Management
            </CardTitle>
            <CardDescription className="text-[#E8E4DE]/60">
              Update the order status and view timeline
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current Status */}
            <div className="flex items-center gap-4 p-4 bg-[#050816]/50 rounded-lg">
              <div className="flex-1">
                <p className="text-sm text-[#E8E4DE]/60 mb-1">Current Status</p>
                <div className="flex items-center gap-2">
                  <Badge className={`${statusConfig.className} text-base px-3 py-1`}>
                    {statusConfig.label}
                  </Badge>
                  {order.intake_complete && (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      Intake Complete
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Status Change Section */}
            {order.status !== 'draft' ? (
              <div className="space-y-4">
                <div className="flex items-end gap-4">
                  <div className="flex-1 space-y-2">
                    <label className="text-sm text-[#E8E4DE]/80">Change Status</label>
                    <Select
                      value={selectedStatus}
                      onValueChange={setSelectedStatus}
                      disabled={updatingStatus}
                    >
                      <SelectTrigger 
                        className="bg-[#050816]/50 border-white/10 text-white"
                        data-testid="status-select"
                      >
                        <SelectValue placeholder="Select new status" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0f1724] border-white/10">
                        {ADMIN_STATUSES.map((status) => (
                          <SelectItem 
                            key={status.value} 
                            value={status.value}
                            className="text-white hover:bg-white/10"
                          >
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={() => handleStatusUpdate()}
                    disabled={updatingStatus || selectedStatus === order.status}
                    className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] disabled:opacity-50"
                    data-testid="save-status-btn"
                  >
                    {updatingStatus ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Status
                  </Button>
                </div>

                {/* Warning if setting to delivered without output files */}
                {selectedStatus === 'delivered' && outputFiles.length === 0 && (
                  <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-yellow-400 shrink-0" />
                    <p className="text-sm text-yellow-400">
                      Deliverables not uploaded yet. Consider uploading output files before marking as delivered.
                    </p>
                  </div>
                )}

                {/* Reopen Order Helper - Shows when order is delivered */}
                {order.status === 'delivered' && (
                  <div className="flex items-center gap-2 p-3 bg-[#050816]/50 border border-white/10 rounded-lg" data-testid="reopen-helper">
                    <Info className="h-5 w-5 text-[#C6A14A] shrink-0" />
                    <p className="text-sm text-[#E8E4DE]/70">
                      Need more revisions? Change status back to <span className="text-blue-400 font-medium">In Progress</span> to reopen.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-3 bg-gray-500/10 border border-gray-500/20 rounded-lg">
                <p className="text-sm text-[#E8E4DE]/60">
                  This order is still in draft status. The customer must submit the order before you can manage its status.
                </p>
              </div>
            )}

            {/* Timestamps Section */}
            <div className="border-t border-white/10 pt-4">
              <p className="text-sm text-[#E8E4DE]/80 mb-3">Status Timeline</p>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="p-3 bg-[#050816]/30 rounded-lg">
                  <p className="text-xs text-[#E8E4DE]/50">Created</p>
                  <p className="text-sm text-white mt-1">{formatDate(order.created_at)}</p>
                </div>
                <div className="p-3 bg-[#050816]/30 rounded-lg">
                  <p className="text-xs text-[#E8E4DE]/50">Submitted</p>
                  <p className="text-sm text-white mt-1">{order.submitted_at ? formatDate(order.submitted_at) : '—'}</p>
                </div>
                <div className="p-3 bg-[#050816]/30 rounded-lg">
                  <p className="text-xs text-[#E8E4DE]/50">Started</p>
                  <p className="text-sm text-white mt-1">{order.started_at ? formatDate(order.started_at) : '—'}</p>
                </div>
                <div className="p-3 bg-[#050816]/30 rounded-lg">
                  <p className="text-xs text-[#E8E4DE]/50">Delivered</p>
                  <p className="text-sm text-white mt-1">{order.delivered_at ? formatDate(order.delivered_at) : '—'}</p>
                </div>
                {order.canceled_at && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg md:col-span-2">
                    <p className="text-xs text-red-400/70">Canceled</p>
                    <p className="text-sm text-red-400 mt-1">{formatDate(order.canceled_at)}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ═══════════════════════════════════════════════════════════════════════════
            SERVICE DETAILS SUMMARY - NEW SECTION
        ═══════════════════════════════════════════════════════════════════════════ */}
        <Card className="bg-[#0f1724] border-white/10" data-testid="service-details-section">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-[#C6A14A]" />
              Service Details
            </CardTitle>
            <CardDescription className="text-[#E8E4DE]/60">
              Customer intake summary and payment information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Order Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-[#C6A14A]" />
                <p className="text-sm font-medium text-white">Order Information</p>
              </div>
              <KeyValueGrid 
                items={[
                  { 
                    label: 'Service Type', 
                    value: SERVICE_LABELS[order.service_type] || order.service_type 
                  },
                  { 
                    label: 'Order Title', 
                    value: order.title || '—' 
                  },
                ]}
              />
              {order.notes && (
                <div className="p-3 bg-[#050816]/30 rounded-lg">
                  <p className="text-xs text-[#E8E4DE]/50 uppercase tracking-wide mb-1">Notes</p>
                  <p className="text-sm text-white whitespace-pre-wrap">{order.notes}</p>
                </div>
              )}
            </div>

            {/* Payment Information */}
            <div className="space-y-3 border-t border-white/10 pt-4">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-[#C6A14A]" />
                <p className="text-sm font-medium text-white">Payment</p>
              </div>
              <KeyValueGrid 
                items={[
                  { 
                    label: 'Payment Status', 
                    value: formatPaymentState(order.payment_state),
                    highlight: order.payment_state === 'paid'
                  },
                  { 
                    label: 'Amount', 
                    value: order.amount_total_cents 
                      ? formatCurrency(order.amount_total_cents, order.currency) 
                      : '—' 
                  },
                  { 
                    label: 'Paid At', 
                    value: order.paid_at ? formatDate(order.paid_at) : '—' 
                  },
                  { 
                    label: 'Payment Mode', 
                    value: order.payment_mode || '—' 
                  },
                ]}
              />
            </div>

            {/* Intake Details (Service-Specific) */}
            <div className="space-y-3 border-t border-white/10 pt-4">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-[#C6A14A]" />
                <p className="text-sm font-medium text-white">Intake Snapshot</p>
                {order.intake_complete && (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                    Complete
                  </Badge>
                )}
              </div>
              <KeyValueGrid 
                items={formatServiceDetails(order.service_type, order.details)}
              />
            </div>

            {/* Files Summary */}
            <div className="space-y-3 border-t border-white/10 pt-4">
              <div className="flex items-center gap-2">
                <FileImage className="h-4 w-4 text-[#C6A14A]" />
                <p className="text-sm font-medium text-white">Files Summary</p>
              </div>
              <KeyValueGrid 
                items={[
                  { 
                    label: 'Reference Files', 
                    value: `${referenceFiles.length} file${referenceFiles.length !== 1 ? 's' : ''}` 
                  },
                  { 
                    label: 'Output Files', 
                    value: `${outputFiles.length} file${outputFiles.length !== 1 ? 's' : ''}` 
                  },
                  { 
                    label: 'Last Upload', 
                    value: files.length > 0 
                      ? formatDate(files.reduce((latest, f) => 
                          !latest || new Date(f.created_at) > new Date(latest) ? f.created_at : latest
                        , null))
                      : '—'
                  },
                  { 
                    label: 'Total Revisions', 
                    value: revisions.length > 0 ? revisions.length : '—' 
                  },
                ]}
              />
            </div>
          </CardContent>
        </Card>

        {/* Plan & Revisions Card */}
        <Card className="bg-[#0f1724] border-white/10" data-testid="plan-revisions-section">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-[#C6A14A]" />
              Plan & Revisions
            </CardTitle>
            <CardDescription className="text-[#E8E4DE]/60">
              Manage revision limits and upload revision files
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Plan Settings */}
            <div className="p-4 bg-[#050816]/50 rounded-lg space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Settings className="h-4 w-4 text-[#C6A14A]" />
                <p className="text-sm font-medium text-white">Plan Settings</p>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#E8E4DE]/80">Plan Code</Label>
                  <Input
                    value={planCode}
                    onChange={(e) => setPlanCode(e.target.value)}
                    placeholder="e.g., standard, premium"
                    className="bg-[#050816]/50 border-white/10 text-white placeholder:text-white/30"
                    data-testid="plan-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#E8E4DE]/80">Max Revisions</Label>
                  <Input
                    type="number"
                    min="0"
                    value={maxRevisions}
                    onChange={(e) => setMaxRevisions(parseInt(e.target.value) || 0)}
                    className="bg-[#050816]/50 border-white/10 text-white"
                    data-testid="max-revisions-input"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleSavePlan}
                    disabled={savingPlan}
                    className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] w-full"
                    data-testid="save-plan-btn"
                  >
                    {savingPlan ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Plan
                  </Button>
                </div>
              </div>
              {order.revision_state && (
                <div className="flex items-center gap-4 pt-2 border-t border-white/10">
                  <div className="text-sm">
                    <span className="text-[#E8E4DE]/60">Current Revision: </span>
                    <span className="text-white font-medium">{order.revision_state.current_revision || 0}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-[#E8E4DE]/60">Requests Used: </span>
                    <span className="text-white font-medium">{order.revision_state.revision_requests_used || 0}</span>
                    <span className="text-[#E8E4DE]/40"> / {order.plan?.max_revisions || 0}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Upload Revision */}
            <div className="p-4 bg-[#050816]/50 rounded-lg space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Upload className="h-4 w-4 text-[#C6A14A]" />
                <p className="text-sm font-medium text-white">Upload Revision</p>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#E8E4DE]/80">Revision Type</Label>
                  <Select
                    value={revisionKind}
                    onValueChange={setRevisionKind}
                    disabled={uploadingRevision}
                  >
                    <SelectTrigger 
                      className="bg-[#050816]/50 border-white/10 text-white"
                      data-testid="revision-kind-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f1724] border-white/10">
                      {REVISION_KINDS.map((kind) => (
                        <SelectItem 
                          key={kind.value} 
                          value={kind.value}
                          className="text-white hover:bg-white/10"
                        >
                          {kind.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#E8E4DE]/80">File</Label>
                  <input
                    ref={revisionFileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleRevisionFileSelect}
                    data-testid="revision-file-input"
                  />
                  <Button
                    onClick={() => revisionFileInputRef.current?.click()}
                    variant="outline"
                    className="w-full border-white/10 text-[#E8E4DE] hover:bg-white/5"
                    disabled={uploadingRevision}
                  >
                    {revisionFile ? (
                      <span className="truncate">{revisionFile.name}</span>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Select File
                      </>
                    )}
                  </Button>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleUploadRevision}
                    disabled={!revisionFile || uploadingRevision}
                    className="bg-green-600 text-white hover:bg-green-700 w-full"
                    data-testid="upload-revision-btn"
                  >
                    {uploadingRevision ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Upload Revision
                  </Button>
                </div>
              </div>
              {uploadingRevision && revisionUploadProgress > 0 && (
                <Progress value={revisionUploadProgress} className="h-2" />
              )}
              {revisionFile && !uploadingRevision && (
                <div className="flex items-center justify-between p-2 bg-[#050816]/30 rounded">
                  <div className="flex items-center gap-2">
                    <File className="h-4 w-4 text-[#C6A14A]" />
                    <span className="text-sm text-white truncate">{revisionFile.name}</span>
                    <span className="text-xs text-[#E8E4DE]/40">({formatFileSize(revisionFile.size)})</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRevisionFile(null)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 p-1 h-auto"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Revisions List */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-white flex items-center gap-2">
                  <Eye className="h-4 w-4 text-[#C6A14A]" />
                  Revision History
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadRevisions}
                  disabled={loadingRevisions}
                  className="text-[#E8E4DE]/60 hover:text-white"
                >
                  {loadingRevisions ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </Button>
              </div>
              
              {revisions.length === 0 ? (
                <div className="text-center py-6 text-[#E8E4DE]/60">
                  <RefreshCw className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No revisions yet</p>
                  <p className="text-sm text-[#E8E4DE]/40 mt-1">Upload a revision file above</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {revisions.map((revision) => {
                    const statusConfig = REVISION_STATUS_CONFIG[revision.status] || REVISION_STATUS_CONFIG.pending;
                    const kindLabel = REVISION_KINDS.find(k => k.value === revision.kind)?.label || revision.kind;
                    
                    return (
                      <div 
                        key={revision.id}
                        className="p-4 bg-[#050816]/30 rounded-lg border border-white/5"
                        data-testid={`revision-item-${revision.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-white font-medium">
                                Revision #{revision.revision_number}
                              </span>
                              <Badge className={statusConfig.className}>
                                {statusConfig.label}
                              </Badge>
                              <Badge variant="outline" className="text-[#C6A14A] border-[#C6A14A]/30">
                                {kindLabel}
                              </Badge>
                            </div>
                            <p className="text-sm text-[#E8E4DE]/60">
                              {revision.original_filename || 'Unknown file'}
                            </p>
                            <p className="text-xs text-[#E8E4DE]/40 mt-1">
                              Uploaded {formatDate(revision.created_at)}
                            </p>
                            {revision.customer_feedback && (
                              <div className="mt-2 p-2 bg-orange-500/10 border border-orange-500/20 rounded">
                                <div className="flex items-center gap-1 text-orange-400 text-xs mb-1">
                                  <MessageSquare className="h-3 w-3" />
                                  Customer Feedback
                                </div>
                                <p className="text-sm text-[#E8E4DE]/80">{revision.customer_feedback}</p>
                              </div>
                            )}
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleDownloadRevision(revision)}
                            className="bg-[#C6A14A]/20 text-[#C6A14A] hover:bg-[#C6A14A]/30"
                            data-testid={`download-revision-${revision.id}`}
                          >
                            <Download className="mr-1 h-4 w-4" />
                            Download
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Reference Files */}
        <Card className="bg-[#0f1724] border-white/10">
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileImage className="h-5 w-5 text-[#C6A14A]" />
              <CardTitle className="text-lg text-white">Reference Files</CardTitle>
              <Badge variant="outline" className="ml-2 text-[#E8E4DE]/60">
                {referenceFiles.length}
              </Badge>
            </div>
            <CardDescription className="text-[#E8E4DE]/60">
              Files uploaded by the customer
            </CardDescription>
          </CardHeader>
          <CardContent>
            {referenceFiles.length === 0 ? (
              <p className="text-center text-[#E8E4DE]/60 py-8">No reference files uploaded</p>
            ) : (
              <div className="space-y-2">
                {referenceFiles.map((file) => (
                  <div 
                    key={file.id}
                    className="flex items-center gap-3 p-3 bg-[#050816]/30 rounded-lg"
                  >
                    <FileImage className="h-5 w-5 text-[#C6A14A] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{file.original_filename}</p>
                      <p className="text-xs text-[#E8E4DE]/40">
                        {formatFileSize(file.size_bytes)} • {formatDate(file.created_at)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleDownload(file)}
                      className="bg-[#C6A14A]/20 text-[#C6A14A] hover:bg-[#C6A14A]/30"
                    >
                      <Download className="mr-1 h-4 w-4" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Output Files */}
        <Card className="bg-[#0f1724] border-white/10" data-testid="deliverables-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <File className="h-5 w-5 text-green-400" />
                <CardTitle className="text-lg text-white">Deliverables</CardTitle>
                <Badge variant="outline" className="ml-2 text-[#E8E4DE]/60">
                  {outputFiles.length}
                </Badge>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
                data-testid="deliverable-file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30"
                size="sm"
                data-testid="upload-deliverable-btn"
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload Files
              </Button>
            </div>
            <CardDescription className="text-[#E8E4DE]/60">
              Completed files for customer (up to 300MB each)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Pending uploads section */}
            {uploadingFiles.length > 0 && (
              <div className="space-y-3 p-4 bg-[#C6A14A]/5 border border-[#C6A14A]/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-[#C6A14A]">
                    Files to upload ({uploadingFiles.length})
                  </p>
                  <Button
                    onClick={handleUploadDeliverables}
                    className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]"
                    size="sm"
                    data-testid="confirm-upload-btn"
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Upload All
                  </Button>
                </div>
                <div className="space-y-2">
                  {uploadingFiles.map((file, index) => {
                    const FileIcon = getFileIcon(file.name);
                    const progress = uploadProgress[index];
                    
                    return (
                      <div key={index} className="flex items-center gap-3 p-3 bg-[#050816]/30 rounded-lg">
                        <FileIcon className="h-5 w-5 text-[#C6A14A] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{file.name}</p>
                          <p className="text-xs text-[#E8E4DE]/40">
                            {formatFileSize(file.size)}
                          </p>
                          {progress !== undefined && (
                            <Progress value={progress} className="h-1 mt-2" />
                          )}
                        </div>
                        {progress === undefined && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeUploadingFile(index)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 p-1 h-auto"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Existing deliverables */}
            {outputFiles.length === 0 && uploadingFiles.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[#E8E4DE]/60">No deliverables yet</p>
                <p className="text-sm text-[#E8E4DE]/40 mt-1">
                  Click "Upload Files" to add deliverables for this order
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {outputFiles.map((file) => {
                  const FileIcon = getFileIcon(file.original_filename);
                  
                  return (
                    <div 
                      key={file.id}
                      className="flex items-center gap-3 p-3 bg-green-500/5 border border-green-500/20 rounded-lg"
                      data-testid={`deliverable-file-${file.id}`}
                    >
                      <FileIcon className="h-5 w-5 text-green-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{file.original_filename}</p>
                        <p className="text-xs text-[#E8E4DE]/40">
                          {formatFileSize(file.size_bytes)} • {formatDate(file.created_at)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleDownload(file)}
                        className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        data-testid={`download-deliverable-${file.id}`}
                      >
                        <Download className="mr-1 h-4 w-4" />
                        Download
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Deliver Confirmation Dialog */}
      <AlertDialog open={showDeliverConfirm} onOpenChange={setShowDeliverConfirm}>
        <AlertDialogContent className="bg-[#0f1724] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-400" />
              Mark as Delivered?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#E8E4DE]/60 space-y-2">
              <p>This indicates <span className="text-green-400 font-medium">final delivery</span> to the customer.</p>
              <p>The customer will no longer be able to request revisions or approve changes.</p>
              {outputFiles.length === 0 && (
                <p className="text-yellow-400 mt-2">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  Warning: No deliverable files have been uploaded yet.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-transparent border-white/10 text-[#E8E4DE] hover:bg-white/5 hover:text-white"
              data-testid="cancel-deliver-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeliver}
              className="bg-green-600 text-white hover:bg-green-700"
              data-testid="confirm-deliver-btn"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Yes, Mark as Delivered
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
